import { z } from 'zod';

import { requestJson } from './requests';
import { ApiResponseError } from './response';

const ticketStatusValues = ['open', 'in_progress', 'awaiting_review', 'resolved', 'closed'] as const;
const ticketPriorityValues = ['low', 'normal', 'high', 'urgent'] as const;
const automationStateValues = ['idle', 'pending', 'awaiting_review', 'resolved'] as const;
const assistantActionTypeValues = [
  'notification.send',
  'ticket.open',
  'ticket.update',
  'ticket.resolve',
  'assignment.update',
  'note.append',
  'draft.create',
] as const;

export type AiStaffTicketStatus = (typeof ticketStatusValues)[number];
export type AiStaffTicketPriority = (typeof ticketPriorityValues)[number];
export type AiAutomationState = (typeof automationStateValues)[number];
export type AiAssistantActionType = (typeof assistantActionTypeValues)[number];

const AiStaffTicketSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string(),
  sessionId: z.string().uuid(),
  source: z.string(),
  channel: z.string().nullable(),
  title: z.string().nullable(),
  externalId: z.string().nullable(),
  status: z.enum(ticketStatusValues),
  priority: z.enum(ticketPriorityValues),
  assignedStaffId: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolvedByStaffId: z.string().nullable(),
  lastActionAt: z.string().nullable(),
  lastMessageAt: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AiStaffTicket = z.infer<typeof AiStaffTicketSchema>;

const AiAssistantActionSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string(),
  sessionId: z.string().uuid(),
  ticketId: z.string().uuid().nullable(),
  actionType: z.enum(assistantActionTypeValues),
  status: z.enum(['queued', 'succeeded', 'failed']),
  channel: z.string().nullable(),
  targetId: z.string().nullable(),
  targetType: z.string().nullable(),
  isDryRun: z.boolean(),
  payload: z.record(z.unknown()),
  result: z.record(z.unknown()),
  errorMessage: z.string().nullable(),
  actorType: z.enum(['assistant', 'staff']),
  actorStaffId: z.string().nullable(),
  executedAt: z.string().nullable(),
  createdAt: z.string(),
  metadata: z.record(z.unknown()),
});

export type AiAssistantAction = z.infer<typeof AiAssistantActionSchema>;

const TicketsListResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    tickets: z.array(AiStaffTicketSchema),
    nextCursor: z.string().nullable(),
  }),
});

const ActionsListResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    actions: z.array(AiAssistantActionSchema),
    nextCursor: z.string().nullable(),
  }),
});

const AiUsageSummarySchema = z.object({
  chats: z.number(),
  tokensIn: z.number(),
  tokensOut: z.number(),
  tokensTotal: z.number(),
  smsSent: z.number(),
  ticketsOpened: z.number(),
  notificationsSent: z.number(),
  actionsTotal: z.number(),
  cacheHits: z.number(),
  cacheHitRate: z.number(),
  handoffs: z.number(),
  handoffRate: z.number(),
  functionCalls: z.record(z.number()),
  includedChats: z.number().nullable(),
  remainingChats: z.number().nullable(),
  quotaStatus: z.enum(['disabled', 'ok', 'warning', 'critical', 'exhausted', 'unlimited']),
  shouldBackoff: z.boolean(),
  overageRateCentsPer1k: z.number(),
  estimatedTokenCostCents: z.number(),
  smsPassThrough: z.boolean(),
  smsMarkupPct: z.number(),
});

const AiUsageSummaryResponseSchema = z.object({
  ok: z.literal(true),
  data: AiUsageSummarySchema,
});

const TicketMutationResponseSchema = z.object({
  ok: z.literal(true),
  data: AiStaffTicketSchema,
});

const SessionStatusResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    session: z.object({
      id: z.string().uuid(),
      automationState: z.enum(automationStateValues),
      lastTicketId: z.string().uuid().nullable(),
    }).passthrough(),
    ticket: AiStaffTicketSchema.nullable(),
  }),
});

const ActionCreateResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    action: AiAssistantActionSchema,
    ticket: AiStaffTicketSchema.nullable(),
    notification: z
      .object({
        id: z.string(),
        status: z.string(),
        provider: z.string(),
        sentAt: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
});

export type AiStaffTicketListResponse = z.infer<typeof TicketsListResponseSchema>['data'];
export type AiAssistantActionListResponse = z.infer<typeof ActionsListResponseSchema>['data'];
export type AiUsageSummary = z.infer<typeof AiUsageSummarySchema>;

const DISABLED_USAGE_SUMMARY: AiUsageSummary = {
  chats: 0,
  tokensIn: 0,
  tokensOut: 0,
  tokensTotal: 0,
  smsSent: 0,
  ticketsOpened: 0,
  notificationsSent: 0,
  actionsTotal: 0,
  includedChats: null,
  remainingChats: null,
  quotaStatus: 'disabled',
  shouldBackoff: false,
  overageRateCentsPer1k: 0,
  estimatedTokenCostCents: 0,
  smsPassThrough: false,
  smsMarkupPct: 0,
};

export interface FetchAiStaffTicketsParams {
  status?: AiStaffTicketStatus;
  assignedStaffId?: string | null | 'unassigned';
  limit?: number;
  cursor?: string;
  signal?: AbortSignal;
}

export const fetchAiStaffTickets = async (
  params: FetchAiStaffTicketsParams = {},
): Promise<AiStaffTicketListResponse> => {
  const search = new URLSearchParams();
  if (params.status) {
    search.set('status', params.status);
  }
  if (params.assignedStaffId !== undefined) {
    search.set('assignedStaffId', params.assignedStaffId ?? 'unassigned');
  }
  if (params.limit) {
    search.set('limit', String(params.limit));
  }
  if (params.cursor) {
    search.set('cursor', params.cursor);
  }

  const response = await requestJson<z.infer<typeof TicketsListResponseSchema>>(
    `/ai-staff/tickets${search.toString() ? `?${search.toString()}` : ''}`,
    {
      signal: params.signal,
      schema: TicketsListResponseSchema,
    },
  );

  return response.data;
};

export interface FetchAiStaffActionsParams {
  sessionId?: string;
  ticketId?: string;
  limit?: number;
  cursor?: string;
  signal?: AbortSignal;
}

export const fetchAiStaffActions = async (
  params: FetchAiStaffActionsParams = {},
): Promise<AiAssistantActionListResponse> => {
  const search = new URLSearchParams();
  if (params.sessionId) {
    search.set('sessionId', params.sessionId);
  }
  if (params.ticketId) {
    search.set('ticketId', params.ticketId);
  }
  if (params.limit) {
    search.set('limit', String(params.limit));
  }
  if (params.cursor) {
    search.set('cursor', params.cursor);
  }

  const response = await requestJson<z.infer<typeof ActionsListResponseSchema>>(
    `/ai-staff/actions${search.toString() ? `?${search.toString()}` : ''}`,
    {
      signal: params.signal,
      schema: ActionsListResponseSchema,
    },
  );

  return response.data;
};

export interface CreateAiStaffTicketPayload {
  sessionId: string;
  title?: string;
  priority?: AiStaffTicketPriority;
  status?: AiStaffTicketStatus;
  assignedStaffId?: string | null;
}

export const createAiStaffTicket = async (
  payload: CreateAiStaffTicketPayload,
): Promise<AiStaffTicket> => {
  const response = await requestJson<z.infer<typeof TicketMutationResponseSchema>>(
    `/ai-staff/sessions/${payload.sessionId}/tickets`,
    {
      method: 'POST',
      body: {
        title: payload.title,
        priority: payload.priority,
        status: payload.status,
        assignedStaffId: payload.assignedStaffId ?? undefined,
      },
      schema: TicketMutationResponseSchema,
    },
  );

  return response.data;
};

export interface UpdateAiStaffSessionStatusPayload {
  sessionId: string;
  ticketId?: string;
  ticketStatus?: AiStaffTicketStatus;
  automationState?: AiAutomationState;
  assignedStaffId?: string | null;
  resolutionNotes?: string;
}

export const updateAiStaffSessionStatus = async (
  payload: UpdateAiStaffSessionStatusPayload,
): Promise<z.infer<typeof SessionStatusResponseSchema>['data']> => {
  const response = await requestJson<z.infer<typeof SessionStatusResponseSchema>>(
    `/ai-staff/sessions/${payload.sessionId}/status`,
    {
      method: 'PATCH',
      body: {
        ticketId: payload.ticketId,
        ticketStatus: payload.ticketStatus,
        automationState: payload.automationState,
        assignedStaffId: payload.assignedStaffId ?? undefined,
        resolutionNotes: payload.resolutionNotes,
      },
      schema: SessionStatusResponseSchema,
    },
  );

  return response.data;
};

export interface RecordAiStaffActionPayload {
  sessionId: string;
  ticketId?: string;
  actionType: AiAssistantActionType;
  dryRun?: boolean;
  channel?: string;
  payload?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export const recordAiStaffAction = async (
  payload: RecordAiStaffActionPayload,
): Promise<z.infer<typeof ActionCreateResponseSchema>['data']> => {
  const response = await requestJson<z.infer<typeof ActionCreateResponseSchema>>(
    `/ai-staff/sessions/${payload.sessionId}/actions`,
    {
      method: 'POST',
      body: {
        ticketId: payload.ticketId,
        actionType: payload.actionType,
        dryRun: payload.dryRun,
        channel: payload.channel,
        payload: payload.payload,
        metadata: payload.metadata,
      },
      schema: ActionCreateResponseSchema,
    },
  );

  return response.data;
};

export const fetchAiStaffUsageSummary = async (options: { signal?: AbortSignal } = {}): Promise<AiUsageSummary> => {
  try {
    const response = await requestJson<z.infer<typeof AiUsageSummaryResponseSchema>>('/ai-staff/usage', {
      signal: options.signal,
      schema: AiUsageSummaryResponseSchema,
    });

    return response.data;
  } catch (error) {
    if (error instanceof ApiResponseError && error.status === 403) {
      if (import.meta.env?.DEV) {
        console.debug('[ai-staff] usage summary disabled; returning fallback payload');
      }
      return DISABLED_USAGE_SUMMARY;
    }
    throw error;
  }
};
